import { useState } from "react";
import { Wrench, AlertTriangle, RotateCcw } from "lucide-react";
import { lohnBeschreibung } from "@/lib/lohnanteil";

/**
 * Lohnkosten (BUT-0030, 29.10.2026): Der Lohn je Position kommt automatisch aus der Art der Position
 * (Regel an einer Stelle: lib/lohnanteil.js). Ralph ändert nur, wo es nicht stimmt – das gilt dann als
 * „von Hand“ und wird nie von der Automatik überschrieben (Hand geht vor).
 * Gleicher Inhalt in der Seitenleiste (ab 1280 px) und unter den Positionen (Handy, schmale Fenster).
 */
const ART_NAME = { leistung: "Leistung", fremd: "Fremdleistung", material: "Material", frei: "Freie Position", hand: "von Hand" };
const eur = (x) => `${(Number(x) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const LohnZeile = ({ zeile, onHand, onAutomatik }) => {
  const [text, setText] = useState(null);   // solange getippt wird, steht der Text des Feldes; sonst der berechnete Wert
  const name = zeile.pos.description?.split(" - ")[0]?.substring(0, 40) || "Position";
  return (
    <div className="bg-card rounded-sm border p-2.5" data-testid={`lohn-zeile-${zeile.index}`} data-art={zeile.art}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <p className="text-xs font-medium truncate" title={zeile.pos.description}>{name}</p>
        <span className={`text-[10px] shrink-0 px-1.5 py-0.5 rounded-full ${zeile.art === "hand" ? "bg-blue-100 text-blue-700" : "bg-muted text-muted-foreground"}`} data-testid={`lohn-art-${zeile.index}`}>{ART_NAME[zeile.art]}</span>
      </div>
      <div className="flex items-center gap-1">
        <input
          type="text" inputMode="decimal"
          value={text ?? zeile.betrag.toFixed(2).replace(".", ",")}
          onChange={(e) => {
            setText(e.target.value);
            const n = parseFloat(e.target.value.replace(",", "."));
            if (Number.isFinite(n) && n >= 0) onHand(zeile.index, n);
          }}
          onBlur={() => setText(null)}
          className="flex-1 min-w-0 border rounded px-2 py-1.5 text-sm text-right font-mono bg-background"
          aria-label={`Lohn für ${name}`}
          data-testid={`lohn-eingabe-${zeile.index}`}
        />
        <span className="text-xs text-muted-foreground">€</span>
        {zeile.art === "hand" && (
          <button type="button" onClick={() => onAutomatik(zeile.index)} title="Wieder automatisch rechnen"
            className="p-1.5 rounded-sm border hover:bg-muted" data-testid={`lohn-automatik-${zeile.index}`}>
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {zeile.pruefen && (
        <p className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-300" data-testid={`lohn-hinweis-${zeile.index}`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> Lohn bitte prüfen
        </p>
      )}
    </div>
  );
};

const LohnkostenSidebar = ({
  zeilen, summe, vatRate, showLohnanteil, setShowLohnanteil, lohnanteilCustom, setLohnanteilCustom,
  onHand, onAutomatik, unten = false,
}) => {
  if (zeilen.length === 0) return null;
  const handSumme = lohnanteilCustom !== "" && lohnanteilCustom != null ? parseFloat(String(lohnanteilCustom).replace(",", ".")) : null;
  const handGesetzt = handSumme != null && Number.isFinite(handSumme) && Math.abs(handSumme - summe) > 0.004;
  const wirksam = handGesetzt ? handSumme : summe;
  const mwst = wirksam * (vatRate / 100);
  const pruefen = zeilen.filter((z) => z.pruefen).length;
  return (
    <div className={unten ? "xl:hidden mx-3 mt-1 mb-24 rounded-sm border bg-card p-3 max-w-[794px] sm:mx-auto" : ""} data-testid={unten ? "lohnkosten-unten" : "lohnkosten-sidebar"}>
      {unten && (
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-1">
          <Wrench className="w-4 h-4 text-primary" /> Lohnkosten
        </h3>
      )}
      <p className="text-[11px] text-muted-foreground mb-3">Wird aus den Positionen übernommen. Nur ändern, wo es nicht stimmt.</p>
      <div className="space-y-2">
        {zeilen.map((z) => <LohnZeile key={z.index} zeile={z} onHand={onHand} onAutomatik={onAutomatik} />)}
      </div>
      {pruefen > 0 && <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300" data-testid="lohn-pruefen-summe">{pruefen} {pruefen === 1 ? "Position" : "Positionen"} ohne Lohn – bitte prüfen.</p>}

      <div className="mt-4 pt-3 border-t space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Lohnanteil netto</span>
          <span className="font-mono font-semibold" data-testid="lohn-summe-netto">{eur(wirksam)}</span>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>zzgl. {vatRate}% MwSt</span><span className="font-mono">{eur(mwst)}</span>
        </div>
        <div className="flex justify-between text-sm font-semibold">
          <span>Gesamt-Lohnsumme</span><span className="font-mono">{eur(wirksam + mwst)}</span>
        </div>
        {lohnBeschreibung(zeilen) && <p className="text-[11px] text-muted-foreground">{lohnBeschreibung(zeilen)}</p>}
        {handGesetzt && (
          <div className="mt-1 rounded-sm border border-blue-200 bg-blue-50 px-2 py-1.5 text-[11px] text-blue-900 dark:bg-blue-950/40 dark:text-blue-200" data-testid="lohn-gesamt-hand">
            Gesamtbetrag von Hand gesetzt (Automatik: {eur(summe)}).{" "}
            <button type="button" className="underline font-medium" onClick={() => setLohnanteilCustom("")} data-testid="lohn-gesamt-automatik">Wieder automatisch</button>
          </div>
        )}
      </div>

      <button type="button" onClick={() => setShowLohnanteil(!showLohnanteil)}
        className={`w-full mt-3 px-3 py-2 text-sm font-medium rounded-sm border transition-colors ${showLohnanteil ? "bg-green-50 border-green-300 text-green-800" : "bg-muted text-muted-foreground"}`}
        data-testid="btn-lohn-ausweisen">
        {showLohnanteil ? "Wird im Dokument ausgewiesen" : "Nicht ausweisen – hier anschalten"}
      </button>
    </div>
  );
};

export { LohnkostenSidebar };
